import React from "react";

/**
 * Shared helpers + small visual components for rendering the multi-platform
 * delivery state of a post.
 *
 * A post in Postmoro can publish to multiple platforms in one go. The data
 * model lives on the parent post document:
 *   - `target_platforms: ["X", "LinkedIn"]`    – the user's selection
 *   - `platform: "LinkedIn"`                    – the *first* delivery
 *                                                 (legacy single-platform
 *                                                 callers still read this)
 *   - `publish_results: {
 *         "X":        { ok, external_post_url, error_message, ... },
 *         "LinkedIn": { ok, ... },
 *         "Instagram":{ ok:false, skipped:true, error_message: "..." }
 *      }`
 *
 * Posts created before this feature shipped have no `target_platforms` or
 * `publish_results` — every helper here MUST fall back safely to the
 * single-platform behavior so legacy rows render exactly as before.
 */

// "x" / "X / Twitter" / "X" / "twitter" -> "X". Used for display only.
export function platformLabel(raw) {
  if (raw == null) return "";
  const s = String(raw).trim();
  const lower = s.toLowerCase();
  if (lower === "x" || lower === "twitter" || lower === "x / twitter") return "X";
  if (lower === "linkedin") return "LinkedIn";
  if (lower === "facebook") return "Facebook";
  if (lower === "instagram") return "Instagram";
  if (lower === "tiktok") return "TikTok";
  return s;
}

/** Return the canonical list of platforms a post targets.
 *  Safe for legacy posts (no `target_platforms`) — falls back to `[platform]`.
 */
export function getPostTargetPlatforms(post) {
  if (!post) return [];
  const tp = Array.isArray(post.target_platforms) ? post.target_platforms : null;
  if (tp && tp.length > 0) return tp.map(platformLabel);
  if (post.platform) return [platformLabel(post.platform)];
  return [];
}

/** Returns true if a post intends to publish to 2+ platforms. */
export function isMultiPlatform(post) {
  return getPostTargetPlatforms(post).length > 1;
}

/** Per-platform color so chips read at a glance. Brand-faithful but muted
 *  so they fit Postmoro's surface palette. */
const PLATFORM_CHIP_CLS = {
  X: "text-[color:var(--text-1)] border-[color:rgba(127,138,163,0.45)] bg-[color:rgba(127,138,163,0.10)]",
  LinkedIn: "text-[#7bb6ff] border-[rgba(123,182,255,0.45)] bg-[rgba(123,182,255,0.08)]",
  Facebook: "text-[#9ab9ff] border-[rgba(154,185,255,0.45)] bg-[rgba(154,185,255,0.08)]",
  Instagram: "text-[#ff9ec7] border-[rgba(255,158,199,0.45)] bg-[rgba(255,158,199,0.08)]",
  TikTok: "text-[color:var(--text-1)] border-[color:rgba(127,138,163,0.45)] bg-[color:rgba(127,138,163,0.10)]",
};

/** Tiny tag for one platform. Used in card rows + editor header. */
export function PlatformChip({ platform, result }) {
  const label = platformLabel(platform);
  const cls = PLATFORM_CHIP_CLS[label] || PLATFORM_CHIP_CLS.X;
  // Tiny status dot if delivery result is known.
  let dot = null;
  if (result) {
    if (result.skipped) {
      dot = (
        <span
          className="h-1.5 w-1.5 rounded-full bg-[color:var(--text-3)]"
          title={result.error_message || "Skipped"}
        />
      );
    } else if (result.ok) {
      dot = <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--success)]" />;
    } else {
      dot = <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--danger)]" />;
    }
  }
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-medium " +
        cls
      }
      data-testid={`platform-chip-${label.toLowerCase()}`}
    >
      {dot}
      {label}
    </span>
  );
}

/** Row of chips for every platform this post targets, optionally with
 *  per-platform delivery dots when `publish_results` is present.
 *
 *  - Multi-platform: render chip per `target_platforms` entry.
 *  - Single-platform: render exactly one chip (visual parity with legacy
 *    "platform name" text). Callers may choose to hide this in the
 *    single-platform case to keep the layout dense — see `hideIfSingle`.
 */
export function PlatformChipList({ post, hideIfSingle = false, className = "" }) {
  const platforms = getPostTargetPlatforms(post);
  if (platforms.length === 0) return null;
  if (hideIfSingle && platforms.length <= 1) return null;
  const results = (post && post.publish_results) || {};
  return (
    <span className={"inline-flex items-center gap-1 flex-wrap " + className}>
      {platforms.map((p) => (
        <PlatformChip key={p} platform={p} result={results[p] || results[platformLabel(p)]} />
      ))}
    </span>
  );
}
